

interface RequestCacheEntry {
  promise: Promise<any>;
  timestamp: number;
}

// Cache for in-flight requests to prevent duplicates
const requestCache = new Map<string, RequestCacheEntry>();
const CACHE_DURATION = 100; // ms - short duration to deduplicate rapid successive calls
let unauthorizedInProgress = false;

export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export function getAuthToken(): string {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new UnauthorizedError('No authentication token found');
  }
  return token;
}

export function handleUnauthorized(): void {
  if (unauthorizedInProgress) return;
  unauthorizedInProgress = true;

  localStorage.clear();
  sessionStorage.clear();

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }

  // Allow future unauthorized handling in long-lived sessions.
  window.setTimeout(() => {
    unauthorizedInProgress = false;
  }, 3000);
}

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  skipCache?: boolean;
}


export async function apiFetch<T = any>(
  url: string,
  options: FetchOptions = {}
): Promise<T> {
  const { skipAuth = false, skipCache = false, ...fetchOptions } = options;

  // Create cache key from URL and method
  const method = fetchOptions.method || 'GET';
  const cacheKey = `${method}:${url}`;

  // Check if there's an in-flight request for this endpoint (for GET requests only)
  if (!skipCache && method === 'GET') {
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.promise;
    }
  }

  // Prepare headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge existing headers
  if (fetchOptions.headers) {
    const existingHeaders = new Headers(fetchOptions.headers);
    existingHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }

  if (!skipAuth) {
    try {
      const token = getAuthToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      handleUnauthorized();
      throw err;
    }
  }

  // Create the request promise
  const requestPromise = (async () => {
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      if (response.status === 401) {
        throw new UnauthorizedError('Session expired. Please login again.');
      }

      // Handle other errors
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || `Request failed: ${response.status}`);
      }

      // Parse response
      const data = await response.json();
      return data;
    } finally {
      // Clean up cache after request completes
      requestCache.delete(cacheKey);
    }
  })();

  // Cache GET requests
  if (!skipCache && method === 'GET') {
    requestCache.set(cacheKey, {
      promise: requestPromise,
      timestamp: Date.now(),
    });
  }

  return requestPromise;
}

/**
 * Clear the request cache (useful for testing or force refresh)
 */
export function clearRequestCache(): void {
  requestCache.clear();
}
